import React, { useState, useEffect } from 'react';
import { File, fileService } from '../../services/fileService';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { formatDateTime } from '../../utils/dateUtils';

interface ViewFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: File[];
  onEditFile: (file: File) => void;
  onDownloadFile: (fileId: number) => void;
}

export const ViewFilesModal: React.FC<ViewFilesModalProps> = ({
  isOpen,
  onClose,
  files,
  onEditFile,
  onDownloadFile,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<{ content: string; mimeType: string } | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Auto-select first file when modal opens
  useEffect(() => {
    if (isOpen && files.length > 0 && !selectedFile) {
      setSelectedFile(files[0]);
    }
  }, [isOpen, files, selectedFile]);

  // Load file content when selected file changes
  useEffect(() => {
    const loadFile = async () => {
      if (!selectedFile) {
        setFileContent(null);
        return;
      }

      setIsLoadingFile(true);
      setFileError(null);

      try {
        const result = await fileService.getFilePreview(selectedFile.id);
        setFileContent(result);
      } catch (error: any) {
        console.error('Failed to load file:', error);
        setFileError(error.message || 'Failed to load file');
      } finally {
        setIsLoadingFile(false);
      }
    };

    loadFile();
  }, [selectedFile]);

  const handleFileClick = (file: File) => {
    setSelectedFile(file);
  };

  const renderFilePreview = () => {
    if (isLoadingFile) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B7F8A] mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading file...</p>
          </div>
        </div>
      );
    }

    if (fileError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-red-600">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>Error: {fileError}</p>
          </div>
        </div>
      );
    }

    if (!fileContent || !selectedFile) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Select a file to view</p>
          </div>
        </div>
      );
    }

    // Render PDF
    if (fileContent.mimeType === 'application/pdf') {
      return (
        <iframe
          src={`data:application/pdf;base64,${fileContent.content}`}
          className="w-full h-full border-0"
          title={selectedFile.file_name}
        />
      );
    }

    // Render Image
    if (fileContent.mimeType.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center h-full p-4">
          <img
            src={`data:${fileContent.mimeType};base64,${fileContent.content}`}
            alt={selectedFile.file_name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    // Unsupported file type
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>Unsupported file type: {fileContent.mimeType}</p>
          <button
            onClick={() => onDownloadFile(selectedFile.id)}
            className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download File
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[900] p-4">
      <div
        className="bg-[#B8DDD0] rounded-3xl p-6 w-[95vw] max-w-[1400px] h-[90vh] shadow-2xl relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-gray-700 hover:text-gray-900 text-2xl font-light z-10"
        >
          ✕
        </button>

        {/* Header */}
        <h2 className={`${POPUP_STYLES.title} mb-4`}>
          Patient Files
        </h2>

        {/* Content - Split View */}
        <div className="flex-1 flex gap-4 overflow-hidden">
          {/* Left Side - File Preview (70%) */}
          <div className="flex-[0_0_70%] bg-white/90 rounded-2xl overflow-hidden">
            {renderFilePreview()}
          </div>

          {/* Right Side - File List (28%) */}
          <div className="flex-[0_0_28%] bg-white/90 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Files ({files.length})</h3>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-2 space-y-2">
              {files.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No files available</p>
                </div>
              ) : (
                files.map((file) => (
                  <div
                    key={file.id}
                    className={`bg-[#d4e8e5] rounded-xl p-3 cursor-pointer transition-all relative ${
                      selectedFile?.id === file.id ? 'ring-2 ring-[#5B7F8A] shadow-md' : 'hover:shadow-sm'
                    }`}
                    onClick={() => handleFileClick(file)}
                  >
                    {/* Icons at top-right */}
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      {/* Edit icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditFile(file);
                        }}
                        className="p-1.5 hover:bg-white/50 rounded transition-colors"
                        title="Edit file"
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      {/* Download icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownloadFile(file.id);
                        }}
                        className="p-1.5 hover:bg-white/50 rounded transition-colors"
                        title="Download file"
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>

                    {/* Content area with padding-right to avoid icon overlap */}
                    <div className="pr-20">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {file.file_type_name || file.file_type}
                        </span>
                        <span className="text-xs text-gray-600 ml-2 shrink-0">
                          {formatDateTime(file.created_at)}
                        </span>
                      </div>
                      {/* Filename and Description on same line */}
                      <div className="text-xs text-gray-700 truncate">
                        <span className="font-medium">{file.file_name}</span>
                        {file.description && (
                          <span className="text-gray-600 ml-1">
                            - {file.description}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
